import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useEffect, useRef, useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import EditAppendix4ToForm from './components/EditAppendix4ToForm';
import EditAppendix4Form from './components/EditAppendix4Form';
import EditReferralForICCMWForm from './components/EditReferralForICCMWForm';
import EditReferrerInformationForm from './components/EditReferrerInformationForm';
import EditParticularsOfApplicantForm from './components/EditParticularsOfApplicantForm';
import EditInfoFamilyMemberForm from './components/EditInfoFamilyMemberForm';
import EditReferralSummaryAndSpecialRemarksForm from './components/EditReferralSummaryAndSpecialRemarksForm';
import EditInformationOfReferringOfficeForm from './components/EditInformationOfReferringOfficeForm';
import {
	useCreateAppendix4Mutation,
	useEditAppendix4Mutation,
	useGetAppendix4DraftQuery,
} from '@/api/forms-management/appendix4';
import { FormManagementStatus } from '@/api/enum/common.enum';

export default function CreateAppendix4Page() {
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.create);
	const navigate = useNavigate();
	const location = useLocation();
	const memberListType = location.pathname.includes('admission-request-list')
		? 'admission-request-list'
		: 'member-list';
	const [createAppendix4] = useCreateAppendix4Mutation();
	const { preAdmissionId, formType, admissionRequestId } = useParams();
	const showModal = useModal();
	const [editAppendix4] = useEditAppendix4Mutation();

	const { data: details }: any = useGetAppendix4DraftQuery(
		{ id: preAdmissionId || admissionRequestId || '', type: formType || '' },
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const appendix4Ref = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const appendix4ToRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const referralForICCMWRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const referrerInformationRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const particularsOfApplicantRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const familyMemberRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const referralSummaryAndSpecialRemarksRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const informationOfReferringOfficeFormRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);

	const handleSubmit = async (type: string) => {
		const results = await Promise.all([
			appendix4Ref.current?.validateForm() || Promise.resolve(false),
			appendix4ToRef.current?.validateForm() || Promise.resolve(false),
			referralForICCMWRef.current?.validateForm() || Promise.resolve(false),
			referrerInformationRef.current?.validateForm() || Promise.resolve(false),
			particularsOfApplicantRef.current?.validateForm() ||
				Promise.resolve(false),
			familyMemberRef.current?.validateForm() || Promise.resolve(false),
			referralSummaryAndSpecialRemarksRef.current?.validateForm() ||
				Promise.resolve(false),
			informationOfReferringOfficeFormRef.current?.validateForm() ||
				Promise.resolve(false),
		]);

		console.log(results);

		const validate = results.some((result) => result === false);
		if (type == 'verify') {
			enqueueSnackbar(
				validate
					? t('snackbar.fieldsVerifiedFalse')
					: t('snackbar.fieldsVerifiedTrue'),
				{
					variant: validate ? 'error' : 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			return;
		}
		if (validate) return;

		const { branchId, userId, fromOfficerInCharge, fromRefNo, fromDate } = {
			...(await appendix4Ref.current?.getFormData()),
		};
		const {
			toOfficerInCharge,
			nameOfOrganization,
			toRefNo,
			toDated,
			toFaxNo,
			toTotalPages,
		} = {
			...(await appendix4ToRef.current?.getFormData()),
		};
		const { nameEn, sex, age, ageEstimated, dateOfBirth, fullAddress } = {
			...(await referralForICCMWRef.current?.getFormData()),
		};
		const {
			nameOfReferrer,
			iccmwWorkerName,
			discussionDate,
			referralIsHis,
			referralIsHer,
			mentalHealth,
			suspectedMentalHealth,
		} = {
			...(await referrerInformationRef.current?.getFormData()),
		};
		const {
			applicantNameEn,
			applicantNameZh,
			homeTel,
			mobileTel,
			hkicNo,
			counselling,
			groupsProgrammes,
			skillTraining,
			caseManagement,
			carerSupport,
			peerSupportService,
			clinicalPsychologicalService,
			childrenServices,
			othersService,
			othersServiceSpecify,
			diagnosisMentalHealth,
			diagnosisMentalHealthSpecify,
			suspectedMentalHealthProblem,
			suspectedMentalHealthProblemSpecify,
			dateOfOnset,
			psychiatricFollowUpClinic,
			conditionalDischarge,
			intensiveCare,
			exIntensiveCare,
			specialCare,
			conventionalCare,
			cps,
			pcp,
			caseManagerName,
			caseManagerTel,
			otherSupportServices,
			suicidalAttempt,
			suicidalTendency,
			violence,
			violenceTendency,
			specialAttentionDetails,
			supportedEmployment,
			shelteredWorkshop,
			residentialService,
			residentialServiceSpecify,
			othersRehabilitation,
			othersRehabilitationSpecify,
			notKnown,
			receivingObtained,
			receivingNotObtained,
			approachObtained,
			approachNotObtained,
		} = {
			...(await particularsOfApplicantRef.current?.getFormData()),
		};
		const {
			familyPrefixMr,
			familyPrefixMrs,
			familyPrefixMs,
			familyNameEn,
			familyNameCh,
			familyTel,
			familyLivingWithApplicantYes,
			familyLivingWithApplicantNo,
			relationshipWithApplicant,
			familyConsentObtained,
			familyConsentNotObtained,
			familyIsHim,
			familyIsHer,
		} = {
			...(await familyMemberRef.current?.getFormData()),
		};
		const { referralSummary } = {
			...(await referralSummaryAndSpecialRemarksRef.current?.getFormData()),
		};
		const {
			referringName,
			referringPost,
			referringTel,
			referringAgency,
			referringFax,
			referringOfficeAddress,
			centreWillContinue,
			noFollowUpAction,
			othersRemarks,
			othersRemarksSpecify,
			contactWorker,
			contactTel,
			contactOfficerInCharge,
		} = {
			...(await informationOfReferringOfficeFormRef.current?.getFormData()),
		};

		const formData = {
			branchId,
			userId,
			fromOfficerInCharge,
			fromRefNo,
			fromDate,
			toOfficerInCharge,
			nameOfOrganization,
			toRefNo,
			toDated,
			toFaxNo,
			toTotalPages,
			nameEn,
			sex,
			age,
			ageEstimated: !!ageEstimated,
			dateOfBirth,
			fullAddress,
			nameOfReferrer,
			iccmwWorkerName,
			discussionDate,
			referralIsHis,
			referralIsHer,
			mentalHealth,
			suspectedMentalHealth,
			applicantNameEn,
			applicantNameZh,
			homeTel,
			mobileTel,
			hkicNo,
			counselling,
			groupsProgrammes,
			skillTraining,
			caseManagement,
			carerSupport,
			peerSupportService,
			clinicalPsychologicalService,
			childrenServices,
			othersService,
			othersServiceSpecify,
			diagnosisMentalHealth,
			diagnosisMentalHealthSpecify,
			suspectedMentalHealthProblem,
			suspectedMentalHealthProblemSpecify,
			dateOfOnset,
			psychiatricFollowUpClinic,
			conditionalDischarge,
			intensiveCare,
			exIntensiveCare,
			specialCare,
			conventionalCare,
			cps,
			pcp,
			caseManagerName,
			caseManagerTel,
			otherSupportServices,
			suicidalAttempt,
			suicidalTendency,
			violence,
			violenceTendency,
			specialAttentionDetails,
			supportedEmployment,
			shelteredWorkshop,
			residentialService,
			residentialServiceSpecify,
			othersRehabilitation,
			othersRehabilitationSpecify,
			notKnown,
			receivingObtained,
			receivingNotObtained,
			approachObtained,
			approachNotObtained,
			familyPrefixMr,
			familyPrefixMrs,
			familyPrefixMs,
			familyNameEn,
			familyNameCh,
			familyTel,
			familyLivingWithApplicantYes,
			familyLivingWithApplicantNo,
			relationshipWithApplicant,
			familyConsentObtained,
			familyConsentNotObtained,
			familyIsHim,
			familyIsHer,
			referralSummary,
			referringName,
			referringPost,
			referringTel,
			referringAgency,
			referringFax,
			referringOfficeAddress,
			centreWillContinue,
			noFollowUpAction,
			othersRemarks,
			othersRemarksSpecify,
			contactWorker,
			contactTel,
			contactOfficerInCharge,
			id: details.id || '',
			formStatus:
				type == 'draft'
					? FormManagementStatus.draft
					: FormManagementStatus.submitted,
		};

		if (!formData.id) delete formData.id;

		if (type == 'submit') {
			showModal((props: any) => (
				<ConfirmationDialog
					{...props}
					showCloseIcon={false}
					title={t('preAdmission.completeFormTitle')}
					titleProps={{ variant: 'h6' }}
					description={
						<Typography variant='body1'>
							{t('preAdmission.completeFormText')}
						</Typography>
					}
					cancellationText={t('button.cancel')}
					confirmationText={t('button.confirm')}
					confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
					otherButtonText={t('button.saveDraft')}
					otherButtonProps={{ type: 'submit', variant: 'contained' }}
					onConfirm={async () => {
						saveApi({
							...formData,
							formStatus: FormManagementStatus.submitted,
						});
					}}
					onOtherConfirm={async () => {
						saveApi({ ...formData, formStatus: FormManagementStatus.draft });
					}}
				/>
			));
		} else if (type == 'draft') {
			saveApi({ ...formData, formStatus: FormManagementStatus.draft });
		}
	};

	const saveApi = async (formData: any) => {
		const response = details.id
			? await editAppendix4(formData)
			: await createAppendix4(formData);
		if ('data' in response) {
			enqueueSnackbar(
				formData.formStatus == FormManagementStatus.submitted
					? t('snackbar.faceSheetSubmitSuccess')
					: t('snackbar.draftFaceSheetSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			navigate(
				preAdmissionId
					? `/pre-admission/pre-admission-list/detail/${preAdmissionId}/forms-management`
					: `/member-management/${memberListType}/detail/${admissionRequestId}/forms-management`
			);
		}
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Card sx={{ marginTop: 3 }}>
					<CardContent sx={{ padding: '24px' }}>
						<Typography variant='h4' marginBottom={'38px'}>
							{t('drawerListItems.newAppendix4')}
						</Typography>
						<EditAppendix4Form
							ref={appendix4Ref}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditAppendix4ToForm
							ref={appendix4ToRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditReferralForICCMWForm
							ref={referralForICCMWRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditReferrerInformationForm
							ref={referrerInformationRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditParticularsOfApplicantForm
							ref={particularsOfApplicantRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditInfoFamilyMemberForm
							ref={familyMemberRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditReferralSummaryAndSpecialRemarksForm
							ref={referralSummaryAndSpecialRemarksRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditInformationOfReferringOfficeForm
							ref={informationOfReferringOfficeFormRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
					</CardContent>

					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button
							type='submit'
							onClick={() =>
								navigate(
									preAdmissionId
										? `/pre-admission/pre-admission-list/detail/${preAdmissionId}/forms-management`
										: `/member-management/${memberListType}/detail/${admissionRequestId}/forms-management`
								)
							}
						>
							{t('button.cancel')}
						</Button>
						<Button
							type='submit'
							variant='contained'
							onClick={() => handleSubmit('draft')}
							endIcon={<EditNoteOutlinedIcon />}
						>
							{t('button.saveDraft')}
						</Button>
						<Button
							type='submit'
							variant='contained'
							onClick={() => handleSubmit('verify')}
							endIcon={<TaskOutlinedIcon />}
						>
							{t('button.verifyFields')}
						</Button>
						<Button
							type='submit'
							variant='contained'
							onClick={() => handleSubmit('submit')}
							endIcon={<CheckCircleOutlineIcon />}
						>
							{t('button.complete')}
						</Button>
					</CardActions>
				</Card>
			</Box>
		</>
	);
}
